import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useIntl } from 'react-intl';
import EmptyData from '../../../../common/emptyData/EmptyData';
import { useChartTranslations } from '../../../../../helpers/useChartTranslations';
import { buildFormattingOptions, formatNumber, buildAnnotationMap, createFieldFormatter } from '../../../../../helpers/fieldValueFormatter';

export const CHART_COLORS = [
  '#7DB3FF',
  '#49457B',
  '#FF7C78',
  '#FED3D0',
  '#6F76D9',
  '#9ADFB4',
  '#2E7987',
];

function GenericBarChart({
  resultSet,
  height = 600,
  colors = CHART_COLORS,
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  reportType,
  fieldAliasMap = {},
  formatting,
  chartOptions,
}) {
  const { translateLabel } = useChartTranslations(resultSet, reportType, fieldAliasMap);
  const intl = useIntl();
  const fmtOptions = buildFormattingOptions(formatting, intl);

  // Apply chartOptions overrides
  const effectiveColors  = chartOptions?.colors?.length ? chartOptions.colors : colors;
  const effectiveGrid    = chartOptions?.axes?.xAxis?.showGrid !== undefined ? chartOptions.axes.xAxis.showGrid : showGrid;
  const effectiveLegend  = chartOptions?.legend?.show !== undefined ? chartOptions.legend.show : showLegend;
  const legendPosition   = chartOptions?.legend?.position || 'bottom';
  const xLabel           = chartOptions?.axes?.xAxis?.label || '';
  const yLabel           = chartOptions?.axes?.yAxis?.label || '';
  const yScale           = chartOptions?.axes?.yAxis?.scale || 'linear';
  const yShowGrid        = chartOptions?.axes?.yAxis?.showGrid !== undefined ? chartOptions.axes.yAxis.showGrid : showGrid;

  const seriesNames = resultSet?.seriesNames ? resultSet.seriesNames() : [];
  const dimKey = resultSet?.loadResponses?.[0]?.dimensions?.[0]?.replace('.', '_');
  const annotationMap = React.useMemo(() => buildAnnotationMap(resultSet), [resultSet]);

  const chartData = useMemo(() => {
    const rawChartData = resultSet?.chartPivot ? resultSet.chartPivot() : [];
    const xFormatter = dimKey ? createFieldFormatter(dimKey, annotationMap, { intl }) : null;
    if (!xFormatter) return rawChartData;
    return rawChartData.map((item) => ({
      ...item,
      x: xFormatter({ [dimKey]: item.x }) || item.x,
    }));
  }, [resultSet, annotationMap, dimKey, intl]);

  if (
    !resultSet ||
    !resultSet.loadResponses?.length ||
    chartData.length === 0
  ) {
    return <EmptyData />;
  }

  // Custom legend formatter
  const legendFormatter = (value) => translateLabel(value);

  // Custom tooltip formatter
  const tooltipFormatter = (value, name) => [formatNumber(value, fmtOptions), translateLabel(name)];
  const yAxisTickFormatter = (value) => formatNumber(value, fmtOptions);

  return (
    <div dir="ltr">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartData} margin={{ bottom: 80 }}>
          {effectiveGrid && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis
            dataKey="x"
            angle={-45}
            textAnchor="end"
            height={100}
            interval={0}
            label={xLabel ? { value: xLabel, position: 'insideBottom', offset: -10 } : undefined}
          />
          <YAxis
            scale={yScale}
            tickFormatter={yAxisTickFormatter}
            label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft' } : undefined}
          />
          {showTooltip && <Tooltip formatter={tooltipFormatter} />}
          {effectiveLegend && <Legend formatter={legendFormatter} verticalAlign={legendPosition} />}
          {seriesNames.map(({ key }, index) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="a"
              fill={effectiveColors[index % effectiveColors.length]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

GenericBarChart.propTypes = {
  resultSet: PropTypes.object.isRequired,
  height: PropTypes.number,
  colors: PropTypes.arrayOf(PropTypes.string),
  showGrid: PropTypes.bool,
  showLegend: PropTypes.bool,
  showTooltip: PropTypes.bool,
  reportType: PropTypes.string,
  formatting: PropTypes.object,
  chartOptions: PropTypes.object,
};

export default GenericBarChart;
